import React, { useEffect, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { axiosInstance } from '../App';
import TwoStepVerificationDialog from './TwoStepVerificationDialog';
import { Button } from './ui/button';

export default function OrganizationMfaPolicy() {
  const [policy, setPolicy] = useState(null);
  const [saving, setSaving] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    axiosInstance.get('/organization/security/mfa').then(({ data }) => setPolicy(data)).catch(() => setLoadError(true));
  }, []);

  if (!policy) return <p role={loadError ? "alert" : undefined} className="text-sm text-gray-500">{loadError ? "Could not load organisation two-step verification settings. Refresh to try again." : "Loading organisation security settings…"}</p>;

  const toggle = async (required) => {
    if (!window.confirm(required ? 'Enable two-step verification for your whole organisation? Everyone, including Tenant Admins, will need an authenticator code at their next sign-in, or be asked to set one up.' : 'Turn off two-step verification for your whole organisation? Nobody in this tenant will need an authenticator code at their next sign-in. Saved authenticators are retained for when you enable it again.')) return;
    setSaving(true);
    try {
      const { data } = await axiosInstance.put('/organization/security/mfa', { required });
      setPolicy((current) => ({ ...current, ...data }));
      toast.success(required ? 'Two-step verification is now required for everyone' : 'Two-step verification is off for your whole organisation');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not update the two-step verification policy');
    } finally { setSaving(false); }
  };

  return (
    <div id="organization-mfa-policy" className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3" data-testid="organization-mfa-policy">
      <ShieldCheck size={18} className="text-slate-500" aria-hidden="true" />
      <div className="min-w-0 flex-1 text-sm">
        <p className="font-medium text-slate-900">Two-step verification</p>
        <p className="text-xs text-slate-500">{policy.available ? 'Off by default. On requires two-step verification for everyone in this organisation, including Tenant Admins. Off stops code prompts for everyone, even if they previously set up an authenticator. Changes apply at the next sign-in.' : 'Not configured on this environment yet.'}</p>
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" className="h-4 w-4 accent-blue-600" checked={Boolean(policy.organization_required)} disabled={saving || (!policy.available && !policy.organization_required)} onChange={(event) => toggle(event.target.checked)} />
        Enable for the whole organisation
      </label>
      {policy.organization_required && <Button variant="outline" onClick={() => setManageOpen(true)}>Your authenticator and recovery codes</Button>}
      <TwoStepVerificationDialog open={manageOpen && policy.organization_required} onOpenChange={setManageOpen} />
    </div>
  );
}
